/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { useFavoriteCollectionAction } from "@packages/kids";
import type { Collection, CollectionSectionItem, Images } from "@packages/sdk";
import { useRequestFavorites } from "@packages/sdk";
import type { DropdownMenuProps } from "@packages/ui";
import {
  AddToQueue,
  ContentMoreMenuHeader,
  DropdownMenu,
  DropdownMenuItem,
  IconButton,
  MoreIcon,
  spacing,
  StarIcon,
} from "@packages/ui";
import type { MouseEvent } from "react";
import { useMemo } from "react";

const dropdownMenuContentStyle: Interpolation = { paddingTop: spacing.l };

type CollectionMoreMenuProps = DropdownMenuProps & {
  collection: Pick<
    Collection,
    | "id"
    | "title"
    | "sessions"
    | "desc"
    | "has_access"
    | "has_video"
    | "prayers_count"
  > &
    Partial<Pick<Collection, "prayers">>;
  imageSrc?: Images;
};

export const CollectionMoreMenu = ({
  collection,
  imageSrc,
  ...props
}: CollectionMoreMenuProps) => {
  const { action: updateFavorite } = useFavoriteCollectionAction({
    id: collection.id,
    has_access: collection.has_access ?? true,
    title: collection.title,
    images: imageSrc,
  });
  const { track } = useAnalytics();

  const { query: favoritesQuery } = useRequestFavorites({
    displayType: "collections",
  });
  const t = useTranslations();

  const favoriteCollectionItem = (favoritesQuery.data?.items ?? []).find(
    (favoriteCollection) => favoriteCollection.item_id === collection.id,
  ) as CollectionSectionItem | undefined;

  const availablePrayersCount = useMemo(
    () =>
      collection.prayers
        ? collection.prayers.filter((prayer) => prayer.is_available).length
        : (collection.prayers_count ?? 0),
    [collection.prayers, collection.prayers_count],
  );

  const isFullyFavorited =
    !!favoriteCollectionItem &&
    !!availablePrayersCount &&
    (favoriteCollectionItem.reference?.prayers_count ?? 0) >=
      availablePrayersCount;

  const onClickFavorite = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    void track({
      event: HallowAnalyticsEvent.TappedCollectionOption,
      properties: {
        collection: collection.id,
        option: isFullyFavorited ? "unfavorite" : "favorite",
      },
    });
    void updateFavorite();
  };

  return (
    <DropdownMenu
      align="end"
      contentCSS={dropdownMenuContentStyle}
      modal={false}
      trigger={
        <IconButton
          icon={<MoreIcon />}
          onClick={(e) => {
            // The stopImmediatePropagation call prevents next top loader from firing unnecessarily
            e.nativeEvent.stopImmediatePropagation();
            e.preventDefault();
          }}
          variant="transparentPrimary"
        />
      }
      {...props}
    >
      <ContentMoreMenuHeader
        imageSrc={imageSrc}
        metadata={collection.sessions}
        subtitle={collection.desc}
        title={collection.title}
        hasVideoContent={collection.has_video}
      />
      <DropdownMenuItem
        onClick={onClickFavorite}
        startIcon={isFullyFavorited ? <StarIcon.Off /> : <StarIcon.Outline />}
      >
        {isFullyFavorited
          ? t("dialog_prayer_options_unfavorite")
          : t("dialog_prayer_options_favorite")}
      </DropdownMenuItem>
      <AddToQueue id={collection.id} type={"collection"} />
    </DropdownMenu>
  );
};
